/**
 * Fetch commands for a gateway device + mutation to issue new commands via backend.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { issueGatewayCommand } from '@/services/gateway';
import type { GatewayCommand } from '@/types/gateway';

export function useGatewayCommands(deviceId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['gateway-commands', deviceId],
    queryFn: async (): Promise<GatewayCommand[]> => {
      if (!deviceId) return [];
      const { data, error } = await supabase
        .from('gateway_commands')
        .select('*')
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw new Error(error.message);
      return (data ?? []) as GatewayCommand[];
    },
    enabled: Boolean(deviceId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const issueMutation = useMutation({
    mutationFn: async ({
      commandType,
      payload,
    }: {
      commandType: string;
      payload?: Record<string, unknown>;
    }) => {
      if (!deviceId) throw new Error('No device ID');
      return issueGatewayCommand(deviceId, commandType, payload ?? {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-commands', deviceId] });
    },
  });

  return {
    commands: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
    issueCommand: issueMutation.mutateAsync,
    isIssuing: issueMutation.isPending,
    issueError: issueMutation.error as Error | null,
  };
}
